import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * SafeBoundary — minimal, elegant React error boundary.
 *
 * Wrap any non-critical sub-tree (e.g. a tab, a card) so a runtime
 * exception inside it CANNOT take down the whole page. The fallback is
 * intentionally sober and stays inside the host tab — the rest of the
 * client detail page keeps working.
 *
 * Why we need this:
 *   Safari on iPad is the strictest engine for date parsing, locale
 *   handling and memory pressure. Any uncaught error in a render path
 *   on a screen WITHOUT a boundary yields the dreaded
 *   "Si è verificato ripetutamente un errore" full-page crash dialog.
 *   With a boundary the error is contained, logged, and the operator
 *   can keep using the rest of the app.
 */
export default class SafeBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the browser console so the operator (or our tests) can
    // copy-paste the stack. Never throws — Safari is fine with console.error.
    try {
      // eslint-disable-next-line no-console
      console.error("[SafeBoundary]", this.props.label || "subtree", error, info?.componentStack);
    } catch { /* noop */ }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      const label = this.props.label || "questa sezione";
      return (
        <div
          className="bg-white border border-rose-200 rounded-md p-6 sm:p-8"
          data-testid="safe-boundary-fallback"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="min-w-0">
              <div className="font-medium text-neutral-900 mb-1">
                Non è stato possibile caricare {label}.
              </div>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Il resto della scheda cliente continua a funzionare normalmente.
                Riprova fra qualche istante — se il problema persiste, riapri la
                scheda dal menu Clienti.
              </p>
              <button
                type="button"
                onClick={this.reset}
                className="mt-4 inline-flex items-center text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                data-testid="safe-boundary-retry"
              >
                Riprova
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
